import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { bindAll } from '@bibliocommons/utils-react';
import { truncateOptionsShape } from '@bibliocommons/bc-prop-types';
import { truncate } from '@bibliocommons/utils-string';

import { THREE_LETTER_TO_TWO_LETTER_LANG_CODES } from 'app/constants/LanguageConstants';
import './BibTitle.scss';

export class BibTitle extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  focusOnTitle() {
    if (this._title) {
      this._title.focus();
    }
  }

  formatText(text) {
    const { truncateOptions } = this.props;
    return truncateOptions ? truncate(text, truncateOptions) : text;
  }

  handleClick() {
    if (typeof this.props.onBibTitleClick === 'function') {
      this.props.onBibTitleClick();
    }
  }

  renderScreenReaderMessage() {
    const { renderScreenReaderMessage, title, format, intl } = this.props;
    const formattedFormat = intl.formatMessage({
      id: `FORMAT.${format.toLowerCase()}`
    });
    const values = { title, format: formattedFormat };

    if (renderScreenReaderMessage) {
      return renderScreenReaderMessage(values);
    }

    return <FormattedMessage id="ada_link_to_item_page" values={values} />;
  }

  renderTitle() {
    const { renderAsLink, target, title, id, primaryLanguage } = this.props;
    const formattedTitleContent = this.formatText(title);

    const lang = THREE_LETTER_TO_TWO_LETTER_LANG_CODES[primaryLanguage] || 'en';

    if (renderAsLink) {
      return (
        <a
          href={`/v2/record/${id}`}
          lang={lang}
          target={target}
          rel="noopener"
          ref={el => (this._title = el)}
          data-test-id={`bib-title-${id}`}
          data-key="bib-title"
          onAuxClick={this.handleClick}
          onClick={this.handleClick}
        >
          <span aria-hidden="true" className="title-content">
            {formattedTitleContent}
          </span>
          <ScreenReaderMessage>{this.renderScreenReaderMessage()}</ScreenReaderMessage>
        </a>
      );
    }

    return (
      <div lang={lang} data-test-id={`bib-title-${id}`} ref={el => (this._title = el)} tabIndex="-1">
        <span aria-hidden="true" className="title-content">
          {formattedTitleContent}
        </span>
        <ScreenReaderMessage>{title}</ScreenReaderMessage>
      </div>
    );
  }

  renderSecondaryTitle() {
    const { secondaryTitle } = this.props;

    if (secondaryTitle) {
      return (
        <div className="secondary-title">
          <span aria-hidden="true">{this.formatText(secondaryTitle)}</span>
          <ScreenReaderMessage>{secondaryTitle}</ScreenReaderMessage>
        </div>
      );
    }

    return null;
  }

  render() {
    const { titleTag, renderIcon, subtitle } = this.props;
    const TitleTag = titleTag;

    return (
      <TitleTag className="cp-title">
        {this.renderTitle()}
        {renderIcon()}
        {this.renderSecondaryTitle()}
        {subtitle && <span className="cp-subtitle">{subtitle}</span>}
      </TitleTag>
    );
  }
}

const injectIntlPropTypes = {};

BibTitle.defaultProps = {
  titleTag: 'h2',
  target: '_parent',
  renderAsLink: true,
  renderIcon: () => null
};

BibTitle.propTypes = {
  title: PropTypes.string,
  secondaryTitle: PropTypes.string,
  subtitle: PropTypes.string,
  id: PropTypes.string,
  format: PropTypes.string,
  primaryLanguage: PropTypes.string,
  titleTag: PropTypes.string,
  target: PropTypes.string,
  renderAsLink: PropTypes.bool,
  renderIcon: PropTypes.func,
  truncateOptions: truncateOptionsShape,
  renderScreenReaderMessage: PropTypes.func,
  onBibTitleClick: PropTypes.func,
  ...injectIntlPropTypes
};

export default injectIntl(BibTitle, { forwardRef: true });
